// hooks/useEnergyConsumptionCsvDownloader.js

import { useLazyGetEnergyConsumptionQuery } from '@/api/energy-analysis/calculateEnergySavingsApiSlice';
import { selectTimeRange } from '@/features/energyxplorer/redux/timeRangeSlice';
import { getSelectedAccount } from '@/redux/selectedAccountSlice';
import { showSnackbar } from '@/redux/snackbarSlice';
import { downLoadCsvFile } from '@/utils/export';
import { useDispatch, useSelector } from 'react-redux';

/**
 * Usage:
 * const { downloadEnergyCsv, isDownloading } = useEnergyConsumptionCsvDownloader();
 * <Button onClick={() => downloadEnergyCsv(node)}>Download</Button>
 *
 * Optional:
 * downloadEnergyCsv(node, { aggregate: 'day', start, end, query: { foo: 'bar' } })
 */
export function useEnergyConsumptionCsvDownloader() {
  const dispatch = useDispatch();
  const selectedAccount = useSelector(getSelectedAccount);
  const timeRange = useSelector(selectTimeRange);
  const [getEnergyConsumptionData, { isFetching: isDownloading }] =
    useLazyGetEnergyConsumptionQuery();

  const downloadEnergyCsv = async (
    node,
    { start = timeRange?.start, end = timeRange?.end, aggregate = 'hour' } = {},
  ) => {
    if (!node?.energyTreeNodeId) {
      console.error('Missing node Id');
      return;
    }
    if (!selectedAccount?.AccountId) {
      console.error('Missing account Id');
      return;
    }
    if (!start || !end) {
      console.error('Missing time range');
      return;
    }

    try {
      const response = await getEnergyConsumptionData({
        accountId: selectedAccount.AccountId,
        energyGroupId: node.energyGroupId,
        nodeId: node.energyTreeNodeId,
        start,
        end,
        aggregate,
        filters: [],
      }).unwrap();

      const rows = (response?.EnergyData || []).map((o) => ({
        timestamp: o.timestamp,
        value: o.sum,
      }));

      if (!rows.length) {
        dispatch(
          showSnackbar({
            message: 'Ingen data i perioden.',
            severity: 'info',
          }),
        );
        return;
      }

      downLoadCsvFile(rows, node.name || 'energy-consumption');
    } catch (error) {
      console.error('Failed to fetch energy data', error);
      dispatch(
        showSnackbar({
          message: 'Der opstod en fejl i indhentingen af forbrugsdata',
          severity: 'error',
        }),
      );
    }
  };

  return { downloadEnergyCsv, isDownloading };
}
